import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import moment from 'moment-timezone';
import * as Store from '@apply/store';
import { Header } from '../Header';
import { useAuthentication } from '../LoginPage';
import { alertModal } from '@apply/ui';

import MyApplicationsPanel from './MyApplicationsPanel';
import NotificationPanel from './NotificationPanel';
import OustandingApplicationGrid from './OustandingApplicationGrid';
import InProgressApplications from './InProgressApplications';
import SubmittedApplications from './SubmittedApplications';
import { FeatureType } from '@apply/constants';

const styles = {
    flexContainer: {
        display: 'flex' as const,
        flexDirection: 'row' as const,
        flexWrap: 'wrap' as const,
        width: '100%',
        justifyContent: 'space-evenly' as const,
    },
    inProgressApplications: {
        display: 'flex' as const,
        flexDirection: 'column' as const,
        flexBasis: '100%',
        flex: '1',
    },
    submittedApplications: {
        display: 'flex' as const,
        flexDirection: 'column' as const,
        flexBasis: '100%',
        flex: '1',
    },
    emptyApplications: {
        width: '200px',
    },
};

export interface HomePageProps {
    actions?: Actions;
    completedApplications?: Store.UserStore.Types.UserApplication[];
    inProgressApplications?: Store.UserStore.Types.UserApplication[];
    noticeCounts?: Store.UserStore.Types.UserNoticeCounts;
}

export interface Actions {
    getUserApplications?: (email: string) => Promise<void>;
}

const formatApplicationDisplay = (application: Store.UserStore.Types.UserApplication) => {
    return `${application.extendedPropertyNames}`;
};

const formatApplicationDateTime = (application: Store.UserStore.Types.UserApplication) => {
    const now = moment();
    const firstday = now.clone().weekday(0);
    const lastday = now.clone().weekday(6);
    const formattedAppLastModified = moment.tz(application.lastModified, 'America/Chicago');
    if (formattedAppLastModified.isBetween(firstday, lastday, null, '[]')) {
        return formattedAppLastModified.format('dddd');
    } else {
        const dt = formattedAppLastModified.format('M/D/YYYY');
        return `${dt}`;
    }
};

const formatApplicationToolTip = (application: Store.UserStore.Types.UserApplication) => {
    const dt = moment.tz(application.lastModified, 'America/Chicago').format('h:mm a');
    return `${dt} CT`;
};

export const HomePage: React.FC<HomePageProps> = ({ actions }) => {
    const { isRegisteredUser, requiresESign, authentication, isFeatureEnabled } = useAuthentication();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string>('');

    const ifDrawsEnabled: boolean = useMemo(() => isFeatureEnabled(FeatureType.Draws), [isFeatureEnabled]);

    useEffect(() => {
        setLoading(true);
        setLoadError('');

        if (authentication?.username && actions?.getUserApplications) {
            actions
                .getUserApplications(authentication.username)
                .then(() => {
                    setLoading(false);
                })
                .catch((err) => {
                    setLoadError(err);
                    setLoading(false);
                });
        }
    }, [authentication?.username, actions]);

    useEffect(() => {
        if (loadError) {
            alertModal({
                message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                onConfirm: () => {
                    setLoadError('');
                },
            });
        }
    }, [loadError]);

    if (requiresESign) {
        return <Navigate to={`/signagreement`} />;
    }

    return (
        <>
            {!loading && isRegisteredUser && authentication?.username ? (
                <div className='app-container home-page-container'>
                    <Header />
                    <main>
                        <NotificationPanel
                            setLoading={setLoading}
                            setLoadError={setLoadError}
                        />
                        <MyApplicationsPanel />
                        {ifDrawsEnabled && <OustandingApplicationGrid />}
                        <div style={styles.flexContainer}>
                            <InProgressApplications
                                styles={styles}
                                formatApplicationDisplay={formatApplicationDisplay}
                                formatApplicationToolTip={formatApplicationToolTip}
                                formatApplicationDateTime={formatApplicationDateTime}
                            />
                            <SubmittedApplications
                                styles={styles}
                                formatApplicationDisplay={formatApplicationDisplay}
                                formatApplicationToolTip={formatApplicationToolTip}
                                formatApplicationDateTime={formatApplicationDateTime}
                            />
                        </div>
                    </main>
                </div>
            ) : (
                <div
                    className='overlay'
                    data-testid='home-page-loading'
                >
                    <div className='overlay__inner'>
                        <div className='overlay__content'>
                            <span className='spinner'></span>
                            <span className='spinner-text'>Loading</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            getUserApplications: (email: string) => dispatch(Store.CrossStore.getUserApplications(email)),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(HomePage);
